// 把 parseOpenApi 的产物（或 AI import 的产物）按 endpoint 合并进现有表单。
//
// 合并语义（取代以前的"全量重建"）：
// - 按 method + 归一化 path 匹配现有 endpoints；归一化做 {param} ↔ :param。
// - 匹配到：以现有 endpoint 为 base，结构字段（path/method/headers/params/
//   pathParams/bodyType/bodySchema/responses/name/description）由 incoming
//   更新；业务字段（costPerCall/billingType/tokenPricing/hidden/endpoint 级
//   privateHeaders/aiSpec*/asyncMode/taskStrategy 等）一律保留。
// - 未匹配：新端点，用 baseFactory(id) 当 base + incoming 结构字段。
// - cur 里有、incoming 没有的：保留，不删除。
//
// param/header 行也按 name 合并：用户填过的 value/description 不被覆盖。
//
// 纯函数，便于被 register.jsx 与 workspace EndpointsManager 共用。

// path 归一化：把 OpenAPI 风格的 `{param}` 与表单内部的 `:param` 都视为相等。
// 同时去掉前后空白、规范化前导斜杠、剥掉 query string。
import { pricingRecordToArray } from './pricing-utils.js';

// Keep the full source contract in the backend's opaque spec wrapper. This
// preserves info extensions through form validation and all revision writes.
export const toOpenApiSpecInput = (contract) =>
  contract && typeof contract === 'object'
    ? { spec: contract.spec ?? contract }
    : undefined;

// Form/template/AI imports keep editable endpoints as the source of truth. A
// full original paths map would otherwise override later edits during export.
export const importedTransportSpecInput = (contract, existing) => {
  const version = (contract?.spec ?? contract)?.info?.['x-xapi-upstream-http-version'];
  const currentSpec = existing?.spec ?? existing ?? {};
  return version === undefined
    ? undefined
    : { spec: { ...currentSpec, info: { ...currentSpec.info, 'x-xapi-upstream-http-version': version } } };
};

export const normalizePathForMatch = (p) => {
  if (!p) return '';
  return String(p)
    .trim()
    .split('?')[0]
    .replace(/\/+$/, '')
    .replace(/\{([^/}]+)\}/g, ':$1')
    .toLowerCase();
};

export const matchKeyOf = (method, path) =>
  `${String(method || '').toUpperCase()} ${normalizePathForMatch(path)}`;

// 行（headers / params / pathParams）归一化为统一形态。
const normalizeRow = (p) => ({
  name: p?.name || '',
  type: p?.type || (p?.schema && p.schema.type) || 'string',
  required: !!p?.required,
  description: p?.description || '',
  value:
    p?.value != null
      ? String(p.value)
      : p?.example != null
      ? String(p.example)
      : p?.default != null
      ? String(p.default)
      : '',
});

export const normalizeRows = (rows) =>
  (Array.isArray(rows) ? rows : []).map(normalizeRow);

// 按 name 合并已有行 + 新行：
// - 已有行被保留；若新行带新的 required/type/description（且原值为空），补上
// - 新行（name 不在已有里）追加在后面
// - 已有里有但新行没有的：保留（用户手写的额外参数不丢）
export const mergeRowsByName = (existingRows, incomingRows) => {
  const existing = (existingRows || []).map((r) =>
    typeof r === 'object' && r !== null ? r : { name: String(r), value: '' },
  );
  const incoming = normalizeRows(incomingRows);
  const byName = new Map();
  for (const row of existing) {
    const key = String(row?.name || '').toLowerCase();
    if (key) byName.set(key, row);
  }
  const merged = [...existing];
  for (const inc of incoming) {
    const key = String(inc.name || '').toLowerCase();
    if (!key) continue;
    const prev = byName.get(key);
    if (prev) {
      // 已有：保留用户填过的 value / description；用 incoming 兜空槽 + 同步 type / required
      prev.type = prev.type || inc.type;
      prev.required = prev.required || inc.required;
      if (!prev.description) prev.description = inc.description;
      if (prev.value == null || prev.value === '') prev.value = inc.value;
    } else {
      merged.push(inc);
      byName.set(key, inc);
    }
  }
  return merged;
};

// 把 incoming（来自 parseOpenApi 或 AI import 的 endpoint dto）合并到一个
// 现有 endpoint。结构字段更新，业务字段保留。
export const mergeEndpoint = (existing, incoming) => {
  const incMethod = String(incoming.method || existing.method || 'GET').toUpperCase();
  const incPath = incoming.path || existing.path || '';
  const inferredBodyType =
    incoming.bodyType ||
    (['POST', 'PUT', 'PATCH'].includes(incMethod) && incoming.bodySchema
      ? 'JSON'
      : existing.bodyType || 'NONE');
  return {
    ...existing, // 业务字段全部保留：costPerCall / billingType / tokenPricing /
                  // hidden / privateHeaders / aiInterfaceSpecId / aiSpecDetectedBy /
                  // aiModelMappings / asyncMode / taskStrategy / upstreamProvider /
                  // fixedHoldAmount / supportsStreaming / streamFormat / userOAuthProviderId
    method: incMethod,
    path: incPath,
    // name / description / group 走"已有为先，空才用 incoming"
    name: existing.name || incoming.name || incoming.summary || incPath,
    description: existing.description || incoming.description || incoming.summary || '',
    // 分组：保留用户已有分组；为空时采用 incoming（OpenAPI tag → group，模板/导入带入）
    group: existing.group || incoming.group || '',
    headers: mergeRowsByName(existing.headers, incoming.headers),
    params: mergeRowsByName(existing.params, incoming.params),
    pathParams: mergeRowsByName(existing.pathParams, incoming.pathParams),
    bodyType: inferredBodyType,
    bodySchema:
      typeof incoming.bodySchema === 'string'
        ? incoming.bodySchema
        : incoming.bodySchema
        ? JSON.stringify(incoming.bodySchema, null, 2)
        : existing.bodySchema || '',
    // 结构化表单字段（multipart/urlencoded）：incoming 显式提供则用，否则保留 existing
    bodyParams: Array.isArray(incoming.bodyParams)
      ? incoming.bodyParams
      : existing.bodyParams || [],
    // responses：若 incoming 显式提供则用 incoming，否则保留 existing
    responses: Array.isArray(incoming.responses) && incoming.responses.length
      ? incoming.responses
      : existing.responses,
  };
};

// 新端点（现有表单里没有匹配项，如"从模板/空白创建"）：结构字段走 mergeEndpoint，
// 但 billing 以 incoming（模板 / 导入的 openapi，parseOpenApi 已解析 x-xapi-billing）为准。
// 这是"从模板创建"时 PER_TOKEN 能落地的关键——mergeEndpoint 里 `...existing` 会用基座
// 默认（PER_CALL + 默认价）盖掉 incoming 的 billing，故新端点需在这里显式回填。
// 注意：仅新端点如此；已匹配的现有端点仍在 mergeEndpoint 里 existing 赢（re-import 不冲掉手工配置）。
const newEndpointFromImport = (base, inc) => {
  const ep = mergeEndpoint(base, inc);
  ep.billingType = inc.billingType || base.billingType;
  ep.costPerCall = inc.costPerCall ?? base.costPerCall;
  ep.costPerResource = inc.costPerResource ?? base.costPerResource;
  ep.resourceCountJsonPath = inc.resourceCountJsonPath || base.resourceCountJsonPath || '';
  ep.estimatedMaxTokens = inc.estimatedMaxTokens || base.estimatedMaxTokens;
  ep.estimatedMaxResources = inc.estimatedMaxResources ?? base.estimatedMaxResources;
  // tokenPricing(record, per-token) → aiConfig.modelPricing(array, per-1M)；tokenJsonPaths 合并。
  const aiConfig = { ...(base.aiConfig || {}), ...(ep.aiConfig || {}) };
  if (inc.tokenPricing && Object.keys(inc.tokenPricing).length > 0) {
    aiConfig.modelPricing = pricingRecordToArray(inc.tokenPricing);
  }
  if (inc.tokenJsonPaths && Object.keys(inc.tokenJsonPaths).length > 0) {
    aiConfig.tokenJsonPaths = { ...(aiConfig.tokenJsonPaths || {}), ...inc.tokenJsonPaths };
  }
  if (inc.supportsStreaming != null) aiConfig.supportsStreaming = !!inc.supportsStreaming;
  if (inc.streamFormat) aiConfig.streamFormat = inc.streamFormat;
  ep.aiConfig = aiConfig;
  return ep;
};

// 主入口：把 incoming endpoints 合并到当前 endpoints。
//
// - 先剔除 cur 里的"空 stub"（path 为空 —— INITIAL_FORM 默认会种一个空
//   endpoint 让 EndpointsSection 渲染表单），避免它在新建 API 流程里残留
// - 按 matchKeyOf 找匹配；匹配到走 mergeEndpoint，否则用 baseFactory(id) 新建
// - cur 里有真实 path 但没在 incoming 里出现的：保留，不删除
// - 新增的 endpoint 追加到末尾
// - 兜底：若合并结果为空（incoming 是空 + cur 全是 stub），返回单个 stub 以保
//   持 endpoints 数组非空（apiFormSchema 校验需要至少 1 个 endpoint）
//
// baseFactory(id: string) 提供初始 endpoint shell（一般传 INITIAL_ENDPOINT）。
export const mergeImportedEndpoints = (currentEndpoints, incomingEndpoints, baseFactory) => {
  const cur = (Array.isArray(currentEndpoints) ? currentEndpoints : [])
    .filter((ep) => ep && String(ep.path || '').trim() !== '');
  const incoming = Array.isArray(incomingEndpoints) ? incomingEndpoints : [];

  // 构建已有 endpoint 的匹配索引（先到先得；重复 method+path 极少见）
  const byKey = new Map();
  cur.forEach((ep, idx) => {
    const key = matchKeyOf(ep.method, ep.path);
    if (!byKey.has(key)) byKey.set(key, idx);
  });

  // 浅复制以便就地更新（避免破坏 react-hook-form 内部引用）
  const result = cur.map((ep) => ({ ...ep }));
  const matchedIndices = new Set();
  const newOnes = [];

  for (const inc of incoming) {
    const key = matchKeyOf(inc.method, inc.path);
    const idx = byKey.get(key);
    if (idx != null && !matchedIndices.has(idx)) {
      result[idx] = mergeEndpoint(result[idx], inc);
      matchedIndices.add(idx);
    } else {
      const base = baseFactory(`e${Date.now().toString(36)}${newOnes.length}`);
      newOnes.push(newEndpointFromImport(base, inc));
    }
  }

  const merged = [...result, ...newOnes];
  return merged.length > 0 ? merged : [baseFactory('e1')];
};
